import { useCallback } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

type MessageValues = Record<string, string | number | boolean | Date | null | undefined>;

/**
 * Like `intl.formatMessage`, but returns an empty string when neither the loaded
 * translation nor the descriptor's `defaultMessage` has any content. Use the result
 * directly in JSX with a truthy check to skip rendering empty elements (so an empty
 * translation never falls back to displaying the translation key).
 */
export function useOptionalMessage() {
	const intl = useIntl();

	return useCallback(
		(descriptor: MessageDescriptor, values?: MessageValues): string => {
			const idKey = descriptor.id as string;
			const raw = idKey ? intl.messages[idKey] : undefined;

			if (typeof raw === 'string' && raw.trim() !== '') {
				return intl.formatMessage(descriptor, values);
			}

			const fallback = typeof descriptor.defaultMessage === 'string' ? descriptor.defaultMessage : '';
			if (!fallback.trim()) {
				return '';
			}
			return values ? intl.formatMessage({ ...descriptor, defaultMessage: fallback }, values) : fallback.trim();
		},
		[intl],
	);
}
